

import { useState, useMemo } from "react";
import { Game } from "@/types";
import { GameCard } from "@/components/campaigns/GameCard";
import { GameDetailSheet } from "@/components/campaigns/GameDetailSheet";
import { useGamesQuery } from "@/hooks/useGamesQuery";
import { useRosterStore } from "@/store/roster-store";
import { useAccountStore } from "@/store/account-store";
import { SkeletonGameCard } from "@/components/ui/Skeleton";

export function CampaignsView() {
  const { data, isLoading, error } = useGamesQuery();
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const rosterEntries = useRosterStore((state) => state.entries);
  const rosterInitialized = useRosterStore((state) => state.initialized);
  const activeAccountId = useAccountStore((state) => state.activeAccountId);

  // Filter out games already claimed by the active account
  const availableGames = useMemo(() => {
    if (!data) return [];
    if (!activeAccountId) return data;
    const claimedGameIds = new Set(
      rosterEntries
        .filter((entry) => entry.accountId === activeAccountId)
        .map((entry) => entry.gameId)
    );

    // Exclude games marked as hidden or archived from the campaigns listing.
    // If a user already claimed a game it will appear in their roster regardless.
    const visibleGames = data.filter((game) => (game.status || 'available') !== 'hidden' && (game.status || 'available') !== 'archived');

    console.log('[CampaignsView] Active account:', activeAccountId);
    console.log('[CampaignsView] Roster entries:', rosterEntries);
    console.log('[CampaignsView] Claimed game IDs:', Array.from(claimedGameIds));
    console.log('[CampaignsView] Visible games (filtered by status):', visibleGames.map(g => g.id));

    return visibleGames.filter((game) => !claimedGameIds.has(game.id));
  }, [data, rosterEntries, activeAccountId]);

  return (
    <section className="space-y-4 pb-24 lg:pb-8">
      <header>
        <p className="text-sm uppercase tracking-wide text-lime-300">Active campaigns</p>
        <h1 className="text-2xl font-semibold text-white lg:text-3xl">Pick a title to fill your slot</h1>
      </header>

      {error && (
        <p className="rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          Unable to load games. The Firebase admin credentials might be missing.
        </p>
      )}

      {!isLoading && !error && rosterInitialized && availableGames.length === 0 && (
        <div className="rounded-2xl border border-lime-500/20 bg-lime-500/10 p-6 text-center">
          <p className="text-sm text-lime-200">
            No campaigns available right now. Check back soon or contact support.
          </p>
        </div>
      )}

      <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0 xl:grid-cols-3">
        {(isLoading || !rosterInitialized) && (
          <>
            <SkeletonGameCard />
            <SkeletonGameCard />
            <SkeletonGameCard />
          </>
        )}
        {!isLoading && rosterInitialized && availableGames.map((game) => (
          <GameCard key={game.id} game={game} onSelect={setSelectedGame} />
        ))}
      </div>

      <GameDetailSheet
        game={selectedGame}
        open={Boolean(selectedGame)}
        onClose={() => setSelectedGame(null)}
      />
    </section>
  );
}
